import {
  CancelledError,
  type Mutation,
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'

import { logger } from '@/modules/utils'

/**
 * Contract for the `meta` attached to a query or mutation to drive central
 * error logging. Reaching for `meta` forces a choice: either supply an
 * `errorMessage` (logged by the central handler) plus `source` (the owning
 * hook, so failures point at it), or set `silent` to opt out and own the
 * logging locally. The wording of `errorMessage` is a convention (stable label;
 * put dynamic values in the query key / mutation variables), but the presence
 * of both `errorMessage` and `source` is enforced by the type.
 */
export type QueryErrorMeta = Record<string, unknown> &
  ({ errorMessage: string; source: string } | { silent: true })

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryErrorMeta
    mutationMeta: QueryErrorMeta
  }
}

const isSilent = (meta: QueryErrorMeta | undefined): boolean =>
  meta !== undefined && 'silent' in meta && meta.silent === true

const messageFor = (
  meta: QueryErrorMeta | undefined,
  fallback: string,
): string =>
  meta !== undefined && typeof meta.errorMessage === 'string'
    ? meta.errorMessage
    : fallback

/**
 * AbortError / TimeoutError (in-flight cancellations, StrictMode double-fires)
 * and react-query's own CancelledError are expected, not failures.
 */
export const isIgnorableError = (error: unknown): boolean =>
  error instanceof CancelledError ||
  (error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'TimeoutError'))

export function logQueryError(
  error: unknown,
  query: Query<unknown, unknown, unknown>,
): void {
  if (isSilent(query.meta) || isIgnorableError(error)) return
  logger.error(error, messageFor(query.meta, 'Query failed'), {
    key: query.queryKey,
    url: window.location.pathname,
    meta: query.meta,
  })
}

const SENSITIVE_KEY = /pin|password|secret|token|credential/i

/**
 * Mask secret-ish values before they reach the console (and, via Faro's console
 * instrumentation, telemetry). Mutation variables can carry a `pin` — user PINs
 * and GxP e-signatures — which must never be logged in the clear.
 */
export const redactSecrets = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(redactSecrets)
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) =>
        SENSITIVE_KEY.test(key)
          ? [key, '[redacted]']
          : [key, redactSecrets(nested)],
      ),
    )
  }
  return value
}

export function logMutationError(
  error: unknown,
  variables: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
): void {
  if (isSilent(mutation.meta) || isIgnorableError(error)) return
  logger.error(error, messageFor(mutation.meta, 'Mutation failed'), {
    key: mutation.options.mutationKey,
    url: window.location.pathname,
    variables: redactSecrets(variables),
    meta: mutation.meta,
  })
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: logQueryError }),
  mutationCache: new MutationCache({
    onError: (error, variables, _onMutateResult, mutation) =>
      logMutationError(error, variables, mutation),
  }),
})
